import type { ReactNode } from 'react'
import { makeAutoObservable } from 'mobx'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import type { SnackbarCloseReason } from '@mui/material/Snackbar'
import type { SvgIconComponent } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import type { GapSnackbarAction, GapSnackbarVariant } from './GapSnackbar'
import { snackbarSurfaceSx, snackbarVariantStyle, type SnackbarVariantStyle } from './snackbarSurface'

/** Filled status icons, tinted to the variant accent, whose glyph is negative space. */
const VARIANT_ICON: Record<GapSnackbarVariant, SvgIconComponent> = {
   info: InfoIcon,
   success: CheckCircleIcon,
   warning: WarningIcon,
   error: ErrorIcon,
}

export interface GapSnackbarStoreProps {
   message: ReactNode
   variant?: GapSnackbarVariant
   icon?: ReactNode | false
   actions: GapSnackbarAction[]
   showClose: boolean
   onClose: () => void
   dark: boolean
   paperColor: string
   shadow: string
}

export default class GapSnackbarStore {
   sp: GapSnackbarStoreProps

   constructor(sp: GapSnackbarStoreProps) {
      this.sp = sp
      makeAutoObservable(this, {}, { autoBind: true })
   }

   /** Colours for the active variant. */
   get style(): SnackbarVariantStyle {
      const { variant, dark, paperColor } = this.sp
      return snackbarVariantStyle({ variant, dark, paperColor })
   }

   get leadingIcon(): ReactNode {
      const { icon, variant } = this.sp
      if (icon === false) return null
      if (icon) return icon
      if (!variant) return null
      const Icon = VARIANT_ICON[variant]
      return <Icon sx={{ fontSize: 22, color: this.style.accent, flexShrink: 0 }} />
   }

   get messageContent(): ReactNode {
      return <>
         {this.leadingIcon}
         <Typography variant="body2" component="span" sx={{ color: this.style.text }}>
            {this.sp.message}
         </Typography>
      </>
   }

   get actionElements(): ReactNode[] {
      const { accent, accentHover, onAccent, text } = this.style
      return this.sp.actions.map((action, index) => {
         if (action.label) {
            const emphasis = action.emphasis ?? 'outlined'
            const buttonSx = emphasis === 'contained'
               ? { bgcolor: accent, color: onAccent, '&:hover': { bgcolor: accentHover } }
               : { color: accent, borderColor: accent, '&:hover': { borderColor: accentHover, bgcolor: alpha(accent, 0.06) } }
            return (
               <Button key={index} size="small" variant={emphasis} startIcon={action.icon} onClick={action.onClick} sx={{ whiteSpace: 'nowrap', ...buttonSx }}>
                  {action.label}
               </Button>
            )
         }
         return (
            <IconButton key={index} size="small" aria-label={action.ariaLabel} onClick={action.onClick} sx={{ color: text }}>
               {action.icon}
            </IconButton>
         )
      })
   }

   get allActions(): ReactNode[] {
      if (!this.sp.showClose) return this.actionElements
      const closeButton = (
         <IconButton key="close" size="small" aria-label="Close" onClick={this.sp.onClose} sx={{ color: this.style.text }}>
            <CloseIcon sx={{ fontSize: 18 }} />
         </IconButton>
      )
      return [...this.actionElements, closeButton]
   }

   get contentSx(): SxProps<Theme> {
      const { variant, dark, paperColor, shadow } = this.sp
      return snackbarSurfaceSx({ variant, dark, paperColor, shadow })
   }

   /** MUI fires onClose for timeouts, escape and clickaway; ignore clickaway so a stray click never dismisses. */
   handleClose(_event: unknown, reason?: SnackbarCloseReason) {
      if (reason === 'clickaway') return
      this.sp.onClose()
   }
}
